/**
 * `wayai admin sandbox exec` — contract for the platform-admin sandbox-execution
 * surface (harness-agents PR 1.5).
 *
 * This is the first surface that runs an arbitrary shell command in a fresh E2B
 * sandbox (create → runCommand → destroy) to exercise the SandboxDriver + the §4
 * egress gate in isolation, before Phase 2 wires the driver into agents. It is
 * platform-admin gated, strict-rate-limited, and audited — see
 * `apps/backend/src/routes/api/admin/sandbox.ts`.
 *
 * The egress policy enum is duplicated here (not imported from the backend's
 * `sandbox/egress.ts`) because `@wayai/core` is RN-safe and must not depend on a
 * Cloudflare-only module. The backend route re-validates the pair
 * (policy, allowlist) via `validateEgressConfig` — the single source of truth for
 * the SSRF-floor rules — so this enum only gates the *shape*, not the policy
 * semantics.
 */
import { z } from 'zod';
import { uuidSchema } from './common';

/**
 * Egress posture for the sandbox — mirrors the backend `EgressPolicy` union
 * (`sandbox/egress.ts`). Kept as a bare enum here; the route runs
 * `validateEgressConfig` for the real rules (isolated-needs-a-host, no all-traffic
 * allowlist entry, …). Omitted ⇒ the driver fails closed to deny-all.
 */
export const sandboxEgressPolicy = z.enum(['open', 'allowlist', 'isolated']);
export type SandboxEgressPolicy = z.infer<typeof sandboxEgressPolicy>;

/** Hard cap on the command string — a testing surface, not a script host. */
export const SANDBOX_EXEC_MAX_CMD_LENGTH = 4096;
/** Hard cap on the number of egress allowlist entries. */
export const SANDBOX_EXEC_MAX_ALLOWLIST = 64;
/**
 * Wall-clock lifetime cap (ms) for a single exec's sandbox. Bounds a runaway
 * command; the driver reaps the sandbox at this deadline regardless. 5 minutes
 * is generous for an isolation smoke test while keeping cost bounded.
 */
export const SANDBOX_EXEC_MAX_TIMEOUT_MS = 300_000;
/** Default sandbox lifetime when the caller omits `timeout_ms`. */
export const SANDBOX_EXEC_DEFAULT_TIMEOUT_MS = 60_000;

/**
 * POST /api/admin/sandbox/exec — run one shell command in a fresh sandbox.
 *
 * `hub_id` + `connection_id` name the E2B Sandbox connection whose api_key is
 * resolved (and decrypted) server-side; the key NEVER appears in the request or
 * response. `egress_policy` omitted ⇒ deny-all (fail-closed) at the driver.
 */
export const adminSandboxExecBody = z.object({
  /** Hub owning the E2B Sandbox connection whose credential we resolve. */
  hub_id: uuidSchema,
  /** The Sandbox (E2B) connection to source the api_key from. */
  connection_id: uuidSchema,
  /** Shell command to run in the sandbox. Required, bounded length. */
  cmd: z
    .string()
    .min(1, 'cmd is required')
    .max(SANDBOX_EXEC_MAX_CMD_LENGTH, `cmd must be <= ${SANDBOX_EXEC_MAX_CMD_LENGTH} chars`),
  /**
   * Egress posture. Omitted ⇒ the driver fails closed to deny-all outbound (the
   * §4 safe default). The route re-validates the (policy, allowlist) pair via
   * `validateEgressConfig`.
   */
  egress_policy: sandboxEgressPolicy.optional(),
  /**
   * Hosts/CIDRs reachable under `allowlist` (or the single control host under
   * `isolated`). Ignored under `open`. Bounded count.
   */
  egress_allowlist: z
    .array(z.string().min(1).max(253))
    .max(SANDBOX_EXEC_MAX_ALLOWLIST, `at most ${SANDBOX_EXEC_MAX_ALLOWLIST} allowlist entries`)
    .optional(),
  /** Sandbox wall-clock lifetime in ms (bounded). */
  timeout_ms: z
    .number()
    .int()
    .min(1)
    .max(SANDBOX_EXEC_MAX_TIMEOUT_MS)
    .optional(),
});

export type AdminSandboxExecBody = z.infer<typeof adminSandboxExecBody>;

/**
 * POST /api/admin/sandbox/exec response. Carries the command outcome + the
 * (opaque) sandbox id for correlation with provider logs. NEVER carries the
 * api_key, encrypted_credentials, or any secret.
 */
export interface AdminSandboxExecResponse {
  data: {
    /** Process exit code — non-zero is a normal outcome (reported, not an error). */
    exit_code: number;
    stdout: string;
    stderr: string;
    /** Provider sandbox id, for correlation with E2B logs. */
    sandbox_id: string;
    /** Whether the sandbox was successfully destroyed (false ⇒ a leak was logged). */
    destroyed: boolean;
  };
}
